import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Box, Button, Flex, Image, Text, keyframes } from '@chakra-ui/react';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import Header from './Header';
import Stats from './Stats';

dayjs.extend(utc);
dayjs.extend(timezone);

const fadeInAnimation = keyframes`
  0% {
    transform: translateY(20px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const fadeAnimation = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 0.5;
  }
`;

const infiniteBackgroundSlide = keyframes`
  from {
    background-position: 0 center;
  }
  to {
    background-position: -160vw center;
  }
`;

const PrizesPage = () => {
  const [animationDuration, setAnimationDuration] = useState(6);
  const [daysLeftThisWeek, setDaysLeftThisWeek] = useState<number>(0);
  const navigate = useNavigate();
  const weekFromDate = (date: Date): string => {
    const contestStart = dayjs('2025-05-05T00:00:00').tz('America/Los_Angeles', true);
    const currentDate = dayjs(date).tz('America/Los_Angeles', true);
    const daysSinceStart = currentDate.diff(contestStart, 'day') + 1;
    const weekNumber = Math.ceil(daysSinceStart / 7);
    return weekNumber.toString();
  };

  useEffect(() => {
    const now = dayjs().tz('America/Los_Angeles', true);
    const dayOfWeek = now.day() === 0 ? 6 : now.day() - 1;
    const daysUntilEndOfWeek = 6 - dayOfWeek + 1;
    setDaysLeftThisWeek(daysUntilEndOfWeek);
  }, []);

  const onButtonClick = () => {
    navigate(`/leaderboard/monthly/${dayjs().format('YYYY-MM-DD')}?friends=false&league=all`);
  };

  const changeAnimationDuration = () => {
    if (window.innerWidth < 768) {
      setAnimationDuration(6);
    } else if (window.innerWidth < 1024) {
      setAnimationDuration(9);
    } else if (window.innerWidth < 1280) {
      setAnimationDuration(12);
    } else if (window.innerWidth < 1440) {
      setAnimationDuration(15);
    } else if (window.innerWidth < 1920) {
      setAnimationDuration(18);
    }
  };

  useEffect(() => {
    changeAnimationDuration();
  }, []);

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="flex-start"
      gap="20px"
      overflow="auto"
      p="0 0 20px"
      gridRow="span 2"
    >
      <Header />
      <Flex flexDirection="column" alignItems="center" gap="12px" marginBottom="16px">
        <Text
          fontSize="24px"
          fontWeight="800"
          lineHeight="1.2"
          textAlign="center"
          css={{ textWrap: 'balance' }}
          opacity="0"
          animation={`${fadeInAnimation} 0.5s ease-out forwards 0.1s`}
        >
          Play SBC and win prizes
        </Text>
        <Text
          fontSize="16px"
          lineHeight="1.2"
          fontWeight="700"
          textAlign="center"
          css={{ textWrap: 'balance' }}
          opacity="0"
          animation={`${fadeInAnimation} 0.5s ease-out forwards 0.2s`}
        >
          Finish #1 in June monthly leaderboard
          <br />
          and win two ClutchPoints tees
        </Text>
      </Flex>

      <Flex
        position="relative"
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        width="100%"
        mb="-40px"
        pb="30px"
      >
        <Box
          top="0%"
          left="0%"
          position="absolute"
          w="100%"
          h="100%"
          boxShadow="inset 0 40px 40px -30px #242424, inset 0 -40px 20px -20px #242424"
          animation={`${infiniteBackgroundSlide} ${animationDuration}s linear infinite`}
          backgroundRepeat="repeat"
          backgroundSize="80%"
          opacity="0.5"
          style={{
            backgroundImage: "url('/sbc-logo-pattern.svg'), url('/sbc-logo-pattern.svg')",
          }}
        />
        <Box position="relative" zIndex="1" w="250px" aspectRatio="1/1" py="15px">
          <Image src="/promo-t-shirt.webp" alt="clutch-quiet-basketball" position="relative" zIndex="1" />
          <Box
            position="absolute"
            top="44px"
            left="26px"
            zIndex="2"
            backgroundColor="rgba(248, 60, 60, 1)"
            borderRadius="20px"
            p="5px 11px 6px"
            lineHeight="1.3"
            fontWeight="700"
            fontSize="16px"
            transform="rotate(-7deg)"
          >
            june
          </Box>
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            boxShadow="0 0 200px 0 rgba(248, 60, 60, 1)"
            w="80px"
            h="80px"
            borderRadius="50%"
          />
        </Box>
      </Flex>
      <Button
        onClick={onButtonClick}
        display="flex"
        flexDirection="column"
        data-cy="join-button"
        borderRadius="16px"
        px="44px"
        isDisabled={true}
        opacity="0.6"
        cursor="not-allowed"
      >
        winner pending
      </Button>
      {/* <Stats /> */}
      <Flex flex="1" alignItems="flex-end">
        <Text
          fontSize="10px"
          lineHeight="1.2"
          fontWeight="500"
          textAlign="center"
          css={{ textWrap: 'balance' }}
          opacity="0"
          animation={`${fadeAnimation} 0.5s ease-out forwards 0.3s`}
          px="15px"
        >
          <Link
            to="https://start-bench-cut.notion.site/Sweepstakes-Rules-1ec02d4df7878012a209f2f1852bb3eb"
            target="_blank"
            style={{
              textDecoration: 'underline',
              color: 'inherit',
            }}
          >
            Official Rules and Terms.
          </Link>{' '}
          Must be U.S. resident 18+. Winner is determined by the top monthly score from June 1-30, 2025.
        </Text>
      </Flex>
    </Flex>
  );
};

export default PrizesPage;
